import { Box, Button, FormControlLabel, Grid, Paper, Radio, RadioGroup, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { checkPollRestricted, fetchPollFields, submitPollResponse } from '../../../backend/backendApis'
import HttpService from '../../../backend/requestApis'
import CustomTextField from '../../CustomTextField'
import './CastPollForm.css'
import { headingBlue, maroon } from '../../../assets/colors'
import { buttonStyle, errorTextStyle } from '../../../utils/commonCSS'
import { validateContact, validateName } from '../../../utils/validations'
import Loader from '../../Loader'
import { ThumbUp, PhoneAndroid, LockOutlined, ArrowBack, HowToVote, Lock } from '@mui/icons-material'
import { sendOtp, verifyOTP } from '../../../utils/commonFunctions'


// --- Helpers for rendering formatted question text with line breaks & bullets ---
const parseBoldText = (str) => {
	if (!str) return '';
	const parts = str.split(/(\*\*.*?\*\*)/g);
	return parts.map((part, i) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return (
				<Typography
					key={i}
					component="strong"
					sx={{ fontWeight: 700, fontSize: 'inherit', color: 'inherit' }}
				>
					{part.slice(2, -2)}
				</Typography>
			);
		}
		return part;
	});
};

const renderFormattedQuestion = (text) => {
	if (!text) return null;
	const raw = typeof text === 'string' ? text.replace(/\\n/g, '\n') : String(text);
	const lines = raw.split(/\r?\n/);
	const nonEmptyCount = lines.filter(l => l.trim()).length;

	return (
		<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
			{lines.map((line, idx) => {
				const trimmed = line.trim();
				if (!trimmed) {
					return <Box key={idx} sx={{ height: '0.35rem' }} />;
				}

				const isBullet = /^[\*\-•]\s+/.test(trimmed);
				const cleanLine = isBullet ? trimmed.replace(/^[\*\-•]\s+/, '') : trimmed;
				const isPollQuestion = /^poll\s*:/i.test(trimmed);

				if (isPollQuestion) {
					return (
						<Box
							key={idx}
							sx={{
								mt: 0.6,
								mb: 0.3,
								p: '0.4rem 0.65rem',
								backgroundColor: '#eff6ff',
								borderRadius: '6px',
								borderLeft: `4px solid ${headingBlue}`,
								width: '100%',
							}}
						>
							<Typography
								sx={{
									fontSize: '0.95rem',
									fontWeight: 700,
									color: headingBlue,
									lineHeight: 1.45,
								}}
							>
								{parseBoldText(cleanLine)}
							</Typography>
						</Box>
					);
				}

				return (
					<Box
						key={idx}
						sx={{
							display: 'flex',
							alignItems: 'flex-start',
							pl: isBullet ? 1 : 0,
						}}
					>
						{isBullet && (
							<Typography
								component="span"
								sx={{
									mr: 0.75,
									color: headingBlue,
									fontSize: '1rem',
									lineHeight: 1.4,
									userSelect: 'none',
									fontWeight: 700,
								}}
							>
								•
							</Typography>
						)}
						<Typography
							sx={{
								fontSize: '0.92rem',
								fontWeight: nonEmptyCount === 1 ? 600 : (isBullet ? 500 : 500),
								color: nonEmptyCount === 1 ? headingBlue : '#334155',
								lineHeight: 1.45,
								wordBreak: 'break-word',
							}}
						>
							{parseBoldText(cleanLine)}
						</Typography>
					</Box>
				);
			})}
		</Box>
	);
};

const CastPollForm = () => {
	const { id } = useParams()
	const navigate = useNavigate()
	const [step, setStep] = useState('contact') // 'contact' | 'otp' | 'questions' | 'alreadyVoted' | 'closed'
	const [isPollClosed, setIsPollClosed] = useState(false)
	const [pollClosedMessage, setPollClosedMessage] = useState('')
	const [isRestrictedPoll, setIsRestrictedPoll] = useState(null)
	const [formDetails, setFormDetails] = useState(null)
	const [selectedValues, setSelectedValues] = useState({})
	const [contactNumber, setContactNumber] = useState('')
	const [enteredOTP, setEnteredOTP] = useState('')
	const [confirmationResult, setConfirmationResult] = useState(null)
	const [firebaseToken, setFirebaseToken] = useState(null)
	const [name, setName] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [nameError, setNameError] = useState('')
	const [contactError, setContactError] = useState('')
	const [otpError, setOtpError] = useState('')
	const [submitError, setSubmitError] = useState('')
	const [apiError, setApiError] = useState('')
	const [formSubmitted, setFormSubmitted] = useState(false)

	const updateSelectedValues = (groupId, value, title) => {
		setSelectedValues((prev) => ({ ...prev, [groupId]: { title: title, value: value } }))
	}

	const updateContactNumber = (value) => {
		setContactNumber(value)
		if (contactError) setContactError('')
		if (apiError) setApiError('')
	}

	const updateName = (value) => {
		setName(value)
		if (nameError) setNameError('')
	}

	const updateOTP = (value) => {
		setEnteredOTP(value)
		if (otpError) setOtpError('')
		if (apiError) setApiError('')
	}

	// Initial poll check on mount
	useEffect(() => {
		if (!id) return
		setIsLoading(true)
		let checkOptions = {
			url: checkPollRestricted,
			method: 'GET',
			params: {
				pollId: id
			}
		}
		HttpService(checkOptions)
			.then(resp => {
				setIsLoading(false)
				const isRestricted = resp?.data?.data?.restriction ?? resp?.data?.restriction
				setIsRestrictedPoll(isRestricted)
			})
			.catch(err => {
				setIsLoading(false)
				console.log("Initial check poll restricted error: ", err)
				const status = err?.response?.status || err?.status
				const msg = err?.response?.data?.message || err?.response?.data?.description || ''
				if (
					status === 403 &&
					(msg.toLowerCase().includes('closed') ||
						err?.response?.data?.description?.toLowerCase().includes('closed'))
				) {
					setIsPollClosed(true)
					setPollClosedMessage(
						err?.response?.data?.message ||
						err?.response?.data?.description ||
						'This poll is closed and no longer accepting responses.'
					)
					setStep('closed')
				}
			})
	}, [id])

	// Fetch poll questions & fields
	const fetchPollQuestions = (token = firebaseToken) => {
		setIsLoading(true)
		let options = {
			url: fetchPollFields + id,
			method: 'GET',
			headers: {
				Authorization: token ? `Bearer ${token}` : null
			}
		}
		HttpService(options)
			.then(resp => {
				setFormDetails(resp?.data?.data)
				setIsLoading(false)
				setStep('questions')
			})
			.catch(err => {
				console.log("Error in fetching poll fields: ", err)
				const status = err?.response?.status || err?.status
				const msg = err?.response?.data?.message || err?.response?.data?.description || ''
				if (
					status === 403 &&
					(msg.toLowerCase().includes('closed') ||
						err?.response?.data?.description?.toLowerCase().includes('closed'))
				) {
					setIsPollClosed(true)
					setPollClosedMessage(
						err?.response?.data?.message ||
						err?.response?.data?.description ||
						'This poll is closed and no longer accepting responses.'
					)
					setStep('closed')
				} else {
					setApiError(
						err?.response?.data?.message ||
						err?.response?.data?.description ||
						'Failed to fetch poll details.'
					)
				}
				setIsLoading(false)
			})
	}

	// Step 1: Check Details button click handler
	const handleCheckDetails = async () => {
		let contactValidation = validateContact(contactNumber)
		if (contactValidation !== '') {
			setContactError(contactValidation)
			return
		}
		setContactError('')
		setApiError('')
		setIsLoading(true)

		try {
			let checkOptions = {
				url: checkPollRestricted,
				method: 'GET',
				params: {
					pollId: id,
					contactNumber: contactNumber
				}
			}
			const resp = await HttpService(checkOptions)
			const isRestricted = resp?.data?.data?.restriction ?? resp?.data?.restriction
			const hasVoted = resp?.data?.data?.hasVoted

			setIsRestrictedPoll(isRestricted)

			// 1. If voted -> screen with message 'you have already voted.'
			if (hasVoted) {
				setIsLoading(false)
				setStep('alreadyVoted')
				return
			}

			// 2. If not voted:
			if (isRestricted) {
				// Restricted -> OTP Flow
				try {
					const otpResult = await sendOtp(contactNumber, '+91')
					setConfirmationResult(otpResult)
					setStep('otp')
					setIsLoading(false)
				} catch (otpErr) {
					console.log("Error sending OTP: ", otpErr)
					if (otpErr?.msg === 'auth/too-many-requests') {
						setApiError('Too many attempts. Please try again after some time.')
					} else {
						setApiError('Failed to send OTP. Please refresh and try again.')
					}
					setIsLoading(false)
				}
			} else {
				// Non Restricted -> fetch Questions directly
				fetchPollQuestions(null)
			}
		} catch (err) {
			console.log("Error checking poll details: ", err)
			const status = err?.response?.status || err?.status
			const msg = err?.response?.data?.message || err?.response?.data?.description || ''
			if (
				status === 403 &&
				(msg.toLowerCase().includes('closed') ||
					err?.response?.data?.description?.toLowerCase().includes('closed'))
			) {
				setIsLoading(false)
				setIsPollClosed(true)
				setPollClosedMessage(
					err?.response?.data?.message ||
					err?.response?.data?.description ||
					'This poll is closed and no longer accepting responses.'
				)
				setStep('closed')
				return
			}
			if (status === 409) {
				// 409 Conflict: Already voted
				setIsLoading(false)
				setStep('alreadyVoted')
				return
			}
			setApiError(
				err?.response?.data?.message ||
				err?.response?.data?.description ||
				'Failed to verify poll details.'
			)
			setIsLoading(false)
		}
	}

	// OTP Flow Step 2: Verify entered OTP
	const handleVerifyOTP = async () => {
		if (!enteredOTP || enteredOTP.trim().length === 0) {
			setOtpError('Please enter the OTP.')
			return
		}
		setOtpError('')
		setApiError('')
		setIsLoading(true)

		try {
			let result = await verifyOTP(confirmationResult, enteredOTP)
			if (result) {
				setFirebaseToken(result)
				// OTP verified successfully -> fetch poll questions passing firebase token
				fetchPollQuestions(result)
			}
		} catch (err) {
			console.log('Error in verifying OTP: ', err)
			if (err?.msg && err?.msg === "auth/invalid-verification-code") {
				setOtpError("Invalid OTP. Please check and try again.")
			} else if (err?.msg && err?.msg === "auth/code-expired") {
				setOtpError("OTP has expired. Please request a new one.")
			} else {
				setOtpError("Error in verifying OTP. Please try again.")
			}
			setIsLoading(false)
		}
	}

	// Resend OTP
	const handleResendOTP = async () => {
		setOtpError('')
		setApiError('')
		setIsLoading(true)
		try {
			const otpResult = await sendOtp(contactNumber, '+91')
			setConfirmationResult(otpResult)
			setIsLoading(false)
		} catch (otpErr) {
			console.log("Error resending OTP: ", otpErr)
			setApiError(otpErr?.msg === 'auth/too-many-requests' ? 'Too many attempts. Please wait.' : 'Failed to resend OTP.')
			setIsLoading(false)
		}
	}

	const validateSubmit = () => {
		let isValid = true
		let nameValidation = validateName(name, 'Name')
		if (nameValidation !== '') {
			isValid = false
			setNameError(nameValidation)
		} else {
			setNameError('')
		}

		let contactValidation = validateContact(contactNumber)
		if (contactValidation !== '') {
			isValid = false
			setContactError(contactValidation)
		} else {
			setContactError('')
		}

		if (!formDetails?.fields || Object.keys(selectedValues).length !== formDetails.fields.length) {
			isValid = false
			setSubmitError('Please answer all the questions.')
		} else {
			setSubmitError('')
		}

		return isValid
	}

	const handleSubmit = () => {
		setIsLoading(true)
		let isValid = validateSubmit()
		if (isValid) {
			let options = {
				url: submitPollResponse,
				method: 'POST',
				payload: {
					programId: formDetails?.id || formDetails?._id || id,
					contactNumber: contactNumber,
					name: name,
					fields: Object.entries(selectedValues).map(([key, value]) => ({
						id: key,
						...value
					}))
				},
				headers: {
					headers: {
						Authorization: firebaseToken
							? `Bearer ${firebaseToken}`
							: (localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null)
					}
				}
			}
			HttpService(options)
				.then(resp => {
					console.log("Response: ", resp)
					setFormSubmitted(true)
					setIsLoading(false)
				})
				.catch(err => {
					console.log("Error : ", err)
					const status = err?.response?.status || err?.status
					const msg = err?.response?.data?.message || err?.response?.data?.description || ''
					if (
						status === 403 &&
						(msg.toLowerCase().includes('closed') ||
							err?.response?.data?.description?.toLowerCase().includes('closed'))
					) {
						setIsLoading(false)
						setIsPollClosed(true)
						setPollClosedMessage(
							err?.response?.data?.message ||
							err?.response?.data?.description ||
							'This poll is closed and no longer accepting votes.'
						)
						setStep('closed')
						return
					}
					if (err?.response?.status === 409 || err?.status === 409) {
						setIsLoading(false)
						setApiError(
							err?.response?.data?.message ||
							err?.response?.data?.description ||
							'You have already voted.'
						)
						return
					}
					setApiError(
						err?.response?.data?.message ||
						err?.response?.data?.description ||
						'Error submitting response.'
					)
					setIsLoading(false)
				})
		} else {
			setIsLoading(false)
		}
	}

	return (
		<>
			{isLoading && <Loader />}
			<Box className='formContainer'>
				<Box className='formOuterBox'>
					<Box className='formImageContainer'>
						<img
							src={require("../../../assets/images/WORK_logo.png")}
							alt="Logo"
							style={{ width: "40%" }}
						/>
					</Box>

					{/* Invisible reCAPTCHA container for Firebase Auth */}
					<div id="recaptcha-container"></div>


					{/* Poll Closed Screen */}
					{!formSubmitted && (step === 'closed' || isPollClosed) && (
						<Paper className='formBodyBox' elevation={1} sx={{ p: 3.5, width: '95%', borderRadius: '0.8rem', textAlign: 'center', border: '1px solid #fecaca', backgroundColor: '#fff' }}>
							<Box sx={{
								width: 58,
								height: 58,
								borderRadius: '50%',
								backgroundColor: '#fee2e2',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								margin: '0 auto 0.8rem auto',
								border: '1px solid #fca5a5'
							}}>
								<Lock sx={{ color: '#dc2626', fontSize: '2rem' }} />
							</Box>
							<Typography sx={{ width: '100%', display: 'flex', justifyContent: 'center', color: headingBlue, fontSize: '1.25rem', fontWeight: 700, mb: 0.5 }}>
								Poll Closed
							</Typography>
							<Typography sx={{ color: maroon, fontSize: '0.92rem', fontWeight: 600, mt: 0.5, mb: 1 }}>
								🔒 {pollClosedMessage || 'This poll is closed and no longer accepting responses.'}
							</Typography>
							<Typography sx={{ color: '#666', fontSize: '0.82rem', maxWidth: '320px', margin: '0 auto' }}>
								Responses for this poll are no longer being accepted by the administrator. Thank you for your interest.
							</Typography>
						</Paper>
					)}

					{/* First Screen: Ask for Contact Number with Check Details button */}
					{!formSubmitted && !isPollClosed && step === 'contact' && (
						<Paper className='formBodyBox' elevation={1} sx={{ p: 2.5, width: '95%', borderRadius: '0.8rem', border: '1px solid #D9D9D9' }}>
							<Box sx={{ width: '100%', textAlign: 'center', mb: 1.5 }}>
								<Box sx={{
									width: 46,
									height: 46,
									borderRadius: '50%',
									backgroundColor: '#e8f0fe',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									margin: '0 auto 0.6rem auto'
								}}>
									<PhoneAndroid sx={{ color: headingBlue, fontSize: '1.5rem' }} />
								</Box>
								<Typography sx={{ color: headingBlue, fontSize: '1.15rem', fontWeight: 700 }}>
									Cast Your Vote
								</Typography>
								<Typography sx={{ color: '#666', fontSize: '0.82rem', mt: 0.4 }}>
									Please enter your contact number to proceed
								</Typography>
							</Box>

							<Box className='formTextField' sx={{ mt: 1, width: '100%' }}>
								<CustomTextField
									value={contactNumber}
									setFunc={updateContactNumber}
									placeHolder='Registered Contact Number'
									width='15rem'
								/>
								<Box style={{ display: 'flex', justifyContent: 'left', width: '14rem', marginTop: '0.2rem' }}>
									<Typography style={{ ...errorTextStyle }}>{contactError}</Typography>
								</Box>
							</Box>

							{apiError && (
								<Box sx={{ width: '100%', textAlign: 'center', mt: 1 }}>
									<Typography style={{ ...errorTextStyle, fontSize: '0.85rem' }}>{apiError}</Typography>
								</Box>
							)}

							<Box className='formButtonBox' sx={{ mt: 1.5 }}>
								<Button
									style={buttonStyle}
									onClick={handleCheckDetails}
									disabled={isLoading}
									sx={{
										width: '15rem',
										height: '2.2rem',
										textTransform: 'none',
										fontSize: '0.9rem',
										fontWeight: 600,
										margin: 0,
										borderRadius: '4px'
									}}
								>
									{isLoading ? 'Checking...' : 'Check Details'}
								</Button>
							</Box>
						</Paper>
					)}

					{/* Already Voted Screen */}
					{!formSubmitted && !isPollClosed && step === 'alreadyVoted' && (
						<Paper className='formBodyBox' elevation={1} sx={{ p: 3, width: '95%', borderRadius: '0.8rem', textAlign: 'center', border: '1px solid #D9D9D9' }}>
							<Box sx={{
								width: 56,
								height: 56,
								borderRadius: '50%',
								backgroundColor: '#fef3c7',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								margin: '0 auto 0.8rem auto'
							}}>
								<HowToVote sx={{ color: '#d97706', fontSize: '2rem' }} />
							</Box>
							<Box style={{ width: '100%' }}>
								<Typography sx={{ display: 'flex', justifyContent: "center", color: headingBlue, fontSize: '1.25rem', fontWeight: 700 }}>
									You have already voted.
								</Typography>
							</Box>
							<Typography sx={{ color: '#666', fontSize: '0.85rem', mt: 0.8 }}>
								Your response for this poll has already been recorded for +91 {contactNumber}.
							</Typography>
							{/* <Box sx={{ mt: 2.5 }}>
								<Button
									style={{ ...buttonStyle, backgroundColor: '#f1f5f9', color: headingBlue, border: '1px solid #cbd5e1' }}
									onClick={() => { setStep('contact'); setContactNumber(''); setApiError('') }}
									sx={{ textTransform: 'none', px: 2, height: '2.2rem', fontWeight: 600 }}
								>
									Try Another Number
								</Button>
							</Box> */}
						</Paper>
					)}

					{/* OTP Flow Step 2: OTP Verification Screen */}
					{!formSubmitted && !isPollClosed && step === 'otp' && (
						<Paper className='formBodyBox' elevation={1} sx={{ p: 2.5, width: '95%', borderRadius: '0.8rem', border: '1px solid #D9D9D9' }}>
							<Box sx={{ width: '100%', textAlign: 'center', mb: 1.5 }}>
								<Box sx={{
									width: 46,
									height: 46,
									borderRadius: '50%',
									backgroundColor: '#e8f0fe',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									margin: '0 auto 0.6rem auto'
								}}>
									<LockOutlined sx={{ color: headingBlue, fontSize: '1.5rem' }} />
								</Box>
								<Typography sx={{ color: headingBlue, fontSize: '1.15rem', fontWeight: 700 }}>
									Verify Your Number
								</Typography>
								<Typography sx={{ color: '#666', fontSize: '0.82rem', mt: 0.4 }}>
									Enter the OTP sent to +91 ******{contactNumber.length >= 4 ? contactNumber.slice(-4) : contactNumber}
								</Typography>
							</Box>

							<Box className='formTextField' sx={{ mt: 1, width: '100%' }}>
								<CustomTextField
									value={enteredOTP}
									setFunc={updateOTP}
									placeHolder='Enter 6-digit OTP'
									width='15rem'
								/>
								{otpError && (
									<Box style={{ display: 'flex', justifyContent: 'left', width: '14rem', marginTop: '0.2rem' }}>
										<Typography style={{ ...errorTextStyle }}>{otpError}</Typography>
									</Box>
								)}
								{apiError && (
									<Box sx={{ width: '100%', textAlign: 'center', mt: 1 }}>
										<Typography style={{ ...errorTextStyle, fontSize: '0.85rem' }}>{apiError}</Typography>
									</Box>
								)}
							</Box>

							<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1, px: 0.5 }}>
								<Typography
									onClick={handleResendOTP}
									sx={{ fontSize: '0.8rem', color: headingBlue, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
								>
									Resend OTP
								</Typography>

								{/* <Typography
									onClick={() => { setStep('contact'); setApiError(''); setOtpError(''); setEnteredOTP('') }}
									sx={{ fontSize: '0.8rem', color: '#666', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
								>
									Change Number
								</Typography> */}
							</Box>

							<Box className='formButtonBox' sx={{ mt: 2 }}>
								<Button
									style={buttonStyle}
									onClick={handleVerifyOTP}
									disabled={isLoading}
									sx={{
										width: '15rem',
										height: '2.2rem',
										textTransform: 'none',
										fontSize: '0.9rem',
										fontWeight: 600,
										margin: 0,
										borderRadius: '4px'
									}}
								>
									{isLoading ? 'Verifying...' : 'Verify OTP'}
								</Button>
							</Box>
						</Paper>
					)}

					{/* Step 3: Poll Questions Form */}
					{!formSubmitted && !isPollClosed && step === 'questions' && (
						<Paper className='formBodyBox'>
							{/* Verified/Entered contact number with Change Number link */}
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 0.5, px: 0.5 }}>
								<Box
									sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: headingBlue }}
									onClick={() => { setStep('contact'); setApiError('') }}
								>
									<ArrowBack sx={{ fontSize: '0.85rem' }} />
									<Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Change Number</Typography>
								</Box>
								<Typography sx={{ fontSize: '0.78rem', color: '#666', fontWeight: 600 }}>
									+91 {contactNumber}
								</Typography>
							</Box>

							<Box className='formHeaderBox'>
								<Typography style={{ color: headingBlue, fontSize: '1.2rem', fontWeight: 600 }}>
									{formDetails?.programName ? formDetails.programName : 'Poll Form'}
								</Typography>
							</Box>

							<Box className='formTextField'>
								<CustomTextField
									value={name}
									setFunc={updateName}
									placeHolder='Your Name'
									width='15rem'
								/>
								<Box style={{ display: 'flex', justifyContent: 'left', width: '14rem' }}>
									<Typography style={{ ...errorTextStyle }}>{nameError}</Typography>
								</Box>
							</Box>

							<Box className='formQuesBox'>
								{formDetails?.fields?.map((field) => {
									return (
										<Paper className='formQuesPaper' key={field.id || field._id} sx={{ p: 1.5, borderRadius: '0.6rem', border: '1px solid #D9D9D9', mt: 1.2 }}>
											<Grid container spacing={1} className='formGridContainer' sx={{ flexDirection: 'column', p: 0.5 }}>
												<Grid item xs={12} sm={12} sx={{ width: '100%', pb: 0.5 }}>
													{renderFormattedQuestion(field['title'])}
												</Grid>
												<Grid item xs={12} sm={12} sx={{ width: '100%', pl: 0.5 }}>
													<RadioGroup
														value={selectedValues[field.id]?.value || ""}
														onChange={(e) =>
															updateSelectedValues(field['id'], e.target.value, field['title'])
														}
													>
														{field?.options?.map(option => (
															<FormControlLabel
																key={option}
																value={option}
																control={<Radio sx={{ padding: '0.3rem', transform: "scale(0.85)" }} />}
																label={<Typography sx={{ fontSize: '0.88rem', color: '#334155' }}>{option}</Typography>}
																sx={{ m: 0, py: 0.2 }}
															/>
														))}
													</RadioGroup>
												</Grid>
											</Grid>
										</Paper>
									)
								})}
							</Box>

							<Box style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '0.5rem' }}>
								<Typography style={{ ...errorTextStyle, fontSize: '0.9rem' }}>{submitError ? submitError : apiError}</Typography>
							</Box>

							<Box className='formButtonBox'>
								<Button style={buttonStyle} onClick={handleSubmit}>SUBMIT</Button>
							</Box>
						</Paper>
					)}

					{/* Step 4: Success Screen */}
					{formSubmitted && (
						<Paper className='formSuccessBox'>
							<ThumbUp style={{ fontSize: '3.5rem', color: headingBlue }} />
							<Typography style={{ color: headingBlue, fontSize: '1.2rem', marginTop: '0.5rem' }}>
								Your vote has been recorded!
							</Typography>
						</Paper>
					)}
				</Box>
			</Box>
		</>
	)
}

export default CastPollForm
